/**
 * Catalog SPA pages — /music, /music/{section}, /genre/{slug}.
 *
 * The Express runtime serves an SSR HTML for these routes on direct hit
 * (see backend/music_ssr.py) so bots see the full page before JS runs.
 * When the user navigates client-side from another SPA page (e.g. Home
 * → /music via the nav bar), React Router renders these components which
 * mirror the SSR content plus full track interactivity (preview / buy /
 * cart / download / ownership badge).
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Loader2, Music4, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCart } from "@/lib/CartContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import FilterBar from "@/components/FilterBar";
import BpmKeyFilter from "@/components/BpmKeyFilter";
import { applyAttrsToParams, attrsFromParams, attrsToApiQuery, hasAttrs } from "@/lib/musicAttrs";
import Breadcrumbs from "@/components/Breadcrumbs";
import { TrackCard, useTrackActions } from "@/components/CatalogPrimitives";
import PromoStrip from "@/components/PromoStrip";
import CollectionCard from "@/components/CollectionCard";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import useInfiniteTracks from "@/lib/useInfiniteTracks";
import FreeAllowancePanel from "@/components/FreeAllowancePanel";
import TermsBlock from "@/components/TermsBlock";
import { AlbumCollectionCard, useAlbums } from "@/pages/Albums";

// Deprecated — kept only as safety net if a translation key resolves to
// undefined (t() itself falls back to the key string).  Every rendered
// path uses the i18n-first helpers below.
const SECTION_TITLES = {
  latest: "Latest Releases",
  premium: "Premium Collection",
  free: "Free Collection",
  originals: "Original Productions",
};

const SECTION_INTRO = {
  latest: "The newest DJ SPY drops — remixes, edits, mashups and originals, sorted freshest first. Preview any track free, buy in full quality.",
  premium: "DJ SPY's premium remixes, edits and originals — festival-ready mixes available for full-quality download after purchase.",
  free: "Free DJ SPY remixes, edits, and originals. Sign in to claim them to your library.",
  originals: "DJ SPY original productions — solo, self-written releases (not remixes). Preview, buy, or download the full catalogue.",
};

const HUB_INTRO = "The DJ SPY catalogue — remixes, edits, mashups, and original productions. Browse the newest drops, filter by category, or dive into a genre.";

function slugify(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function setHead(title, description, canonicalPath) {
  document.title = title;
  const site = "https://djspyofficial.com";
  const canonical = `${site}${canonicalPath}`;

  const setMeta = (name, content) => {
    let el = document.querySelector(`meta[name="${name}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute("name", name);
      document.head.appendChild(el);
    }
    el.setAttribute("content", content);
  };
  setMeta("description", description);
  setMeta("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");

  let link = document.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", canonical);
}

function PageHeader({ kicker, title, lead, testidPrefix }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid={`${testidPrefix}-hero`}
    >
      <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// {kicker}</p>
      <h1
        data-testid={`${testidPrefix}-title`}
        className="font-anton mt-3 text-5xl md:text-7xl lg:text-8xl leading-none"
      >
        {title}
      </h1>
      <p
        data-testid={`${testidPrefix}-intro`}
        className="mt-4 text-white/70 max-w-2xl text-base md:text-lg leading-relaxed"
      >
        {lead}
      </p>
    </motion.div>
  );
}

function TrackGrid({ tracks, actions, cart, onDone }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3.5" data-testid="catalog-track-grid">
      {tracks.map((t, i) => (
        <TrackCard
          key={t.id}
          track={t}
          index={i}
          playingId={actions.playingId}
          onPlayToggle={actions.onPlayToggle}
          onBuy={(track) => actions.onBuy(track, { onDone })}
          onDownload={actions.onDownload}
          onAddToCart={actions.onAddToCart}
          buying={actions.buying}
          downloadingId={actions.downloadingId}
          inCart={cart.hasId(t.id)}
        />
      ))}
    </div>
  );
}

function EmptyState({ label, testid }) {
  return (
    <div
      data-testid={testid}
      className="mt-14 hairline rounded-2xl p-14 text-center text-white/50"
    >
      <Music4 size={44} className="mx-auto text-white/20 mb-4" />
      {label}
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  /music — collection landing shell (Premium + Free)                  */
/* -------------------------------------------------------------------- */
// /music: BPM/Key/Camelot filter; tracks are fetched only once a filter is chosen.
function HubAttrFilter({ actions }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const cart = useCart();
  const attrs = useMemo(() => attrsFromParams(searchParams), [searchParams]);
  const active = hasAttrs(attrs);
  const filters = useMemo(() => ({ section: "", attrsQs: active ? attrsToApiQuery(attrs) : "" }), [attrs, active]);
  const { tracks, isLoading, reload, sentinelRef } = useInfiniteTracks(filters, { pageSize: 24, enabled: active });
  return (
    <div className="mt-8" data-testid="music-hub-attr-filter">
      <BpmKeyFilter value={attrs} onChange={(a) => setSearchParams(applyAttrsToParams(searchParams, a), { replace: true })} testid="music-hub-bpm-key" />
      {active && (
        <div className="mt-5" data-testid="music-hub-attr-results">
          {isLoading && tracks.length === 0 && <Loader2 size={16} className="animate-spin text-white/50" />}
          {!isLoading && tracks.length === 0 && (
            <div className="hairline rounded-2xl p-6 text-center text-white/50 text-sm" data-testid="music-hub-attr-empty">{t("musicAttrs.noMatch", { defaultValue: "No tracks match these BPM / key filters." })}</div>
          )}
          <TrackGrid tracks={tracks} actions={actions} cart={cart} onDone={reload} />
          <div ref={sentinelRef} aria-hidden className="h-1 w-full" />
        </div>
      )}
    </div>
  );
}

export function MusicHub() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const [genres, setGenres] = useState([]);
  const actions = useTrackActions();
  const albums = useAlbums();

  useEffect(() => {
    setHead(
      t("seoHead.musicHub.title"),
      t("seoHead.musicHub.description"),
      lang === "ar" ? "/ar/music" : "/music",
    );
  }, [t, lang]);

  // Genre list is a lightweight ~cents call — cheap enough to load
  // eagerly so the "Or dive into a genre" grid renders without a spinner.
  // No track catalogue is fetched on the landing.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/tracks/genres");
        if (alive) setGenres(Array.isArray(res.data) ? res.data : []);
      } catch { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, []);

  const cards = [
    {
      variant: "premium",
      href: "/music/premium",
      kicker: t("music.landing.premiumKicker"),
      title: t("music.landing.premiumTitle"),
      lead: t("music.landing.premiumLead"),
      cta: t("music.landing.premiumCta"),
      testid: "music-landing-premium",
    },
    {
      variant: "free",
      href: "/music/free",
      kicker: t("music.landing.freeKicker"),
      title: t("music.landing.freeTitle"),
      lead: t("music.landing.freeLead"),
      cta: t("music.landing.freeCta"),
      testid: "music-landing-free",
    },
    {
      variant: "edits",
      href: "/music/edits",
      kicker: t("music.landing.editsKicker"),
      title: t("music.landing.editsTitle"),
      lead: t("music.landing.editsLead"),
      cta: t("music.landing.editsCta"),
      testid: "music-landing-edits",
    },
  ];

  return (
    <>
      <Nav />
      <main
        data-testid="page-music-hub"
        className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10"
      >
        <div className="max-w-[1400px] mx-auto">
          <VerifyEmailBanner />
          <PageHeader
            kicker={t("music.kicker").replace(/^\/\/\s*/, "")}
            title={t("music.title")}
            lead={t("music.lead")}
            testidPrefix="music-hub"
          />
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Music", path: "/music" },
            ]}
            testid="music-hub-crumbs"
          />

          <PromoStrip />

          <HubAttrFilter actions={actions} />

          <div
            className={`mt-8 grid grid-cols-1 ${albums.length ? "md:grid-cols-2" : "md:grid-cols-3"} gap-5 sm:gap-6`}
            data-testid="music-landing-collection-grid"
          >
            {cards.map((c) => (
              <CollectionCard
                key={c.href}
                variant={c.variant}
                to={localisedTo(c.href, lang)}
                kicker={c.kicker}
                title={c.title}
                lead={c.lead}
                cta={c.cta}
                testid={c.testid}
              >
                {/* Reserved slot for future server-provided free allowance */}
                {c.testid === "music-landing-free" && (
                  <div
                    data-testid="music-landing-free-allowance-slot"
                    aria-hidden
                    className="mt-4 h-5"
                  />
                )}
              </CollectionCard>
            ))}
            {albums.map((a) => <AlbumCollectionCard key={a.id} album={a} />)}
          </div>

          <section className="mt-16" data-testid="music-hub-genre-section">
            <header className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
              <div>
                <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// EXPLORE</p>
                <h2 className="font-anton text-3xl md:text-5xl mt-2">
                  {t("music.landing.genreHeading")}
                </h2>
              </div>
              <Link
                to={localisedTo("/arabic-remixes", lang)}
                className="font-mono text-[11px] tracking-[0.3em] text-red-brand hover:underline"
                data-testid="music-hub-arabic-remixes-link"
              >
                {lang === "ar" ? "ريمكسات عربية →" : "ARABIC REMIXES HUB →"}
              </Link>
            </header>
            {genres.length === 0 ? (
              <div className="hairline rounded-2xl p-8 text-center text-white/50" data-testid="music-hub-genre-empty">
                {t("music.landing.genreEmpty")}
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="music-hub-genre-grid">
                {genres.map((g) => {
                  const slug = slugify(g.genre);
                  return (
                    <Link
                      key={slug}
                      to={localisedTo(`/genre/${slug}`, lang)}
                      data-testid={`music-hub-genre-card-${slug}`}
                      className="block hairline rounded-xl px-4 py-3 bg-white/[0.02] hover:border-red-brand/60 transition-colors"
                    >
                      <p className="font-display text-base text-white leading-tight">{g.genre}</p>
                      <p className="font-mono text-[10px] tracking-widest text-white/40 mt-1">
                        {g.count} TRACK{g.count === 1 ? "" : "S"}
                      </p>
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        </div>
        <actions.AudioElement />
      </main>
      <Footer />
    </>
  );
}

const EDITS_CHIP_KEYS = new Set(["khaleeji", "iraqi", "lebanese", "syrian", "maghribi", "masri", "hopee", "funky"]);

/* -------------------------------------------------------------------- */
/*  /music/{section}                                                    */
/* -------------------------------------------------------------------- */
export function MusicSection() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { section } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Deep-link preselect (e.g. Arabic Remixes regional cards → /music/edits?edit_category=iraqi).
  const urlEditCategory = section === "edits" && EDITS_CHIP_KEYS.has(searchParams.get("edit_category") || "")
    ? searchParams.get("edit_category") : "";
  const [genres, setGenres] = useState([]);
  const [filters, setFilters] = useState({ category: "", genre: "", q: "", edit_category: urlEditCategory });
  const [originalsOn, setOriginalsOn] = useState(false);
  const [originalsAvailable, setOriginalsAvailable] = useState(false);
  const actions = useTrackActions();
  const cart = useCart();
  const { user } = useAuth();

  // Only /music/premium, /music/free and /music/edits are first-class
  // collections. Any other section (latest, originals) still renders as
  // a filtered list for backwards compatibility, but the primary UX is
  // collection-scoped.
  const collectionKey = section === "premium" ? "premium"
    : section === "free" ? "free"
    : section === "edits" ? "edits"
    : null;

  const heading = collectionKey
    ? t(`music.section.${collectionKey}Title`)
    : t(`catalog.sections.${section}`, { defaultValue: SECTION_TITLES[section] });
  const intro = collectionKey
    ? t(`music.section.${collectionKey}Lead`)
    : t(`catalog.sectionIntros.${section}`, { defaultValue: SECTION_INTRO[section] });

  useEffect(() => {
    if (!heading) {
      navigate("/music", { replace: true });
      return;
    }
    // Locale-aware canonical + head. Heading + intro come from i18n
    // (music.section.*Title / *Lead) so Arabic routes already carry
    // fully-translated titles and descriptions.
    const canonical = lang === "ar" ? `/ar/music/${section}` : `/music/${section}`;
    setHead(`${heading} — DJ SPY Official`, intro, canonical);
    // Reset section-scoped filters + originals toggle when URL section changes.
    setFilters({ category: "", genre: "", q: "", edit_category: urlEditCategory });
    setOriginalsOn(false);
    setOriginalsAvailable(false);
    // urlEditCategory is read on section change only; chip clicks keep the URL in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, heading, intro, navigate, lang]);

  // Effective filters combine the URL collection (`section`) with the
  // in-page filters.  For /music/edits the chip row is more subtle:
  //   * Regional chips (Khaleeji..Hopee) → `edit_category` narrows to
  //     that value AND the backend also forces `track_type=edit` so
  //     Funky can never leak in.
  //   * The "Funky" chip → pass `trackType=funky` which overrides the
  //     catalogue $in and returns Funky tracks only.
  //   * "All" → neither filter set; the catalogue $in returns both types.
  const userKey = user?.id || "anon";
  const isEdits = collectionKey === "edits";
  const editsChip = isEdits ? (filters.edit_category || "") : "";
  const editsTrackType =
    isEdits && editsChip === "funky" ? "funky" : "";
  const editsCategoryParam =
    isEdits && editsChip && editsChip !== "funky" ? editsChip : "";
  const attrs = useMemo(() => attrsFromParams(searchParams), [searchParams]);
  const attrsQs = attrsToApiQuery(attrs);
  const onAttrsChange = (a) => setSearchParams(applyAttrsToParams(searchParams, a), { replace: true });
  const effectiveFilters = useMemo(
    () => ({
      section: section === "latest" ? "" : section,
      genre: filters.genre,
      q: filters.q,
      trackType: collectionKey && originalsOn
        ? "original"
        : editsTrackType,
      edit_category: editsCategoryParam,
      userKey,
      attrsQs,
    }),
    [section, filters.genre, filters.q, editsCategoryParam, editsTrackType, collectionKey, originalsOn, userKey, attrsQs],
  );

  const {
    tracks,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    reload,
    sentinelRef,
  } = useInfiniteTracks(effectiveFilters, { pageSize: 24 });

  const loadGenres = async () => {
    try {
      const res = await api.get("/tracks/genres");
      setGenres(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
  };

  // Cheap Originals availability probe — only fires for Premium/Free
  // collections, only once per section change. Uses limit=1 so the
  // response is a single-row header, not a page of tracks.
  useEffect(() => {
    if (!collectionKey) { setOriginalsAvailable(false); return; }
    let alive = true;
    (async () => {
      try {
        const res = await api.get(`/tracks?category=${collectionKey}&track_type=original&limit=1`);
        if (alive) setOriginalsAvailable(Array.isArray(res.data) && res.data.length > 0);
      } catch { if (alive) setOriginalsAvailable(false); }
    })();
    return () => { alive = false; };
  }, [collectionKey]);

  useEffect(() => { loadGenres(); }, []);
  // NOTE: no separate `useEffect(reload, [user])` — user identity is
  // part of `effectiveFilters` above, so `useInfiniteTracks` refetches
  // exactly once when auth settles (never a duplicate of the initial fetch).

  const onFilterChange = (next) => setFilters(next);

  if (!heading) return null;

  const searchPlaceholder = collectionKey === "premium" || collectionKey === "free"
    ? t(`music.section.search${collectionKey === "premium" ? "Premium" : "Free"}`)
    : undefined;

  return (
    <>
      <Nav />
      <main
        data-testid={`page-music-${section}`}
        className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10"
      >
        <div className="max-w-[1400px] mx-auto">
          <VerifyEmailBanner />
          <PageHeader
            kicker={`MUSIC / ${section.toUpperCase()}`}
            title={heading.toUpperCase()}
            lead={intro}
            testidPrefix={`music-section-${section}`}
          />
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Music", path: "/music" },
              { name: heading, path: `/music/${section}` },
            ]}
            testid="music-section-crumbs"
          />

          {/* B3: server-authoritative free-download balance hero. Only shown
              on /music/free — the Premium collection intentionally omits it
              to keep the paid path uncluttered. */}
          {collectionKey === "free" && (
            <div className="mt-6" data-testid="music-section-free-balance">
              <FreeAllowancePanel />
            </div>
          )}

          {/* Originals toggle appears ONLY when the current collection
              contains at least one original track. */}
          {collectionKey && originalsAvailable && (
            <div
              className="mt-6 inline-flex rounded-full border border-white/10 p-0.5 bg-black/40"
              role="tablist"
              aria-label="Originals filter"
              data-testid={`music-section-${section}-originals-toggle`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={!originalsOn}
                onClick={() => setOriginalsOn(false)}
                data-testid={`music-section-${section}-originals-off`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full transition ${
                  !originalsOn ? "bg-red-brand text-black" : "text-white/60 hover:text-white"
                }`}
              >
                {t("music.section.originalsAll")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={originalsOn}
                onClick={() => setOriginalsOn(true)}
                data-testid={`music-section-${section}-originals-on`}
                className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full transition inline-flex items-center gap-1 ${
                  originalsOn ? "bg-red-brand text-black" : "text-white/60 hover:text-white"
                }`}
              >
                <Sparkles size={11} /> {t("music.section.originalsToggle")}
              </button>
            </div>
          )}

          <FilterBar
            value={filters}
            onChange={onFilterChange}
            genres={genres}
            hideGenre={isEdits}
            lockCategory
            searchPlaceholder={searchPlaceholder}
            testidPrefix={`section-${section}-filter`}
            resultCount={tracks.length}
          />
          <BpmKeyFilter value={attrs} onChange={onAttrsChange} testid={`section-${section}-bpm-key`} />

          {/* Phase A — Edits & Funky category chips.  Rendered ONLY on
              /music/edits; picks a subset via the shared `edit_category`
              server-side filter.  "All" is the empty-string default. */}
          {collectionKey === "edits" && (
            <div
              className="mt-4 flex flex-wrap gap-2"
              role="tablist"
              aria-label={t("music.editsFilter.aria")}
              data-testid="music-section-edits-chips"
            >
              {[
                { key: "",           labelKey: "music.editsFilter.all" },
                { key: "khaleeji",   labelKey: "music.editsFilter.khaleeji" },
                { key: "iraqi",      labelKey: "music.editsFilter.iraqi" },
                { key: "lebanese",   labelKey: "music.editsFilter.lebanese" },
                { key: "syrian",     labelKey: "music.editsFilter.syrian" },
                { key: "maghribi",   labelKey: "music.editsFilter.maghribi" },
                { key: "masri",      labelKey: "music.editsFilter.masri" },
                { key: "hopee",      labelKey: "music.editsFilter.hopee" },
                { key: "funky",      labelKey: "music.editsFilter.funky" },
              ].map((chip) => {
                const active = (filters.edit_category || "") === chip.key;
                return (
                  <button
                    key={chip.key || "all"}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => {
                      setFilters((f) => ({ ...f, edit_category: chip.key }));
                      const next = applyAttrsToParams(new URLSearchParams(), attrs);
                      if (chip.key) next.set("edit_category", chip.key);
                      setSearchParams(next, { replace: true });
                    }}
                    data-testid={`music-section-edits-chip-${chip.key || "all"}`}
                    className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full transition border ${
                      active
                        ? "bg-red-brand text-black border-red-brand"
                        : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
                    }`}
                  >
                    {t(chip.labelKey)}
                  </button>
                );
              })}
            </div>
          )}

          {isLoading && tracks.length === 0 && !error && (
            <div className="mt-6 flex items-center gap-3 text-white/50 font-mono text-xs tracking-widest" data-testid="music-section-loading">
              <Loader2 size={14} className="animate-spin" /> LOADING…
            </div>
          )}
          {error && (
            <div className="mt-6 hairline rounded-xl p-6 bg-red-brand/5 border-red-brand/30 text-red-brand text-sm" data-testid="music-section-error">
              {error}
            </div>
          )}

          {!isLoading && tracks.length === 0 && (filters.genre || filters.q || hasAttrs(attrs)) && (
            <EmptyState
              label="No tracks match your filters in this section."
              testid={`music-section-${section}-empty-filtered`}
            />
          )}

          {!isLoading && tracks.length === 0 && !filters.genre && !filters.q && !hasAttrs(attrs) && (
            <EmptyState label="No tracks in this section yet." testid={`music-section-${section}-empty`} />
          )}

          {tracks.length > 0 && (
            <div className="mt-4">
              {/* Paid catalogues: show the shared Terms consent above the
                  grid so the customer sees it before hitting Buy. Free
                  claims and re-downloads are not affected — they never
                  reach Stripe. */}
              {(collectionKey === "premium" || collectionKey === "edits") && (
                <div className="mb-6" data-testid={`music-section-${section}-terms`}>
                  <TermsBlock testId={`music-section-${section}-terms-checkbox`} />
                </div>
              )}
              <TrackGrid tracks={tracks} actions={actions} cart={cart} onDone={reload} />
              <div
                ref={sentinelRef}
                data-testid={`music-section-${section}-sentinel`}
                aria-hidden
                className="h-1 w-full"
              />
              {isLoadingMore && (
                <div
                  data-testid={`music-section-${section}-loading-more`}
                  className="mt-8 flex items-center justify-center gap-3 text-white/50 font-mono text-[10px] tracking-widest"
                >
                  <Loader2 size={14} className="animate-spin" /> LOADING MORE…
                </div>
              )}
              {!hasMore && (
                <div
                  data-testid={`music-section-${section}-end`}
                  className="mt-10 text-center text-white/30 font-mono text-[10px] tracking-widest"
                >
                  — END OF LIST —
                </div>
              )}
            </div>
          )}
        </div>
        <actions.AudioElement />
      </main>
      <Footer />
    </>
  );
}

/* -------------------------------------------------------------------- */
/*  /genre/{slug}                                                       */
/* -------------------------------------------------------------------- */
export function GenrePage() {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [filters, setFilters] = useState({ category: "", genre: "", q: "" });
  const actions = useTrackActions();
  const cart = useCart();
  const { user } = useAuth();

  const load = async () => {
    try {
      const res = await api.get(`/genre/${encodeURIComponent(slug)}`);
      setData(res.data);
      // Prefer Arabic SEO fields when available; fall back to English
      // record + a natural Arabic template when browsing /ar/genre/*.
      const display = res.data.display_name || res.data.slug;
      let title = res.data.seo_title;
      let desc = res.data.meta_description;
      if (lang === "ar") {
        title = res.data.seo_title_ar || `ريمكسات ${display} من دي جي سباي — DJ SPY Official`;
        desc = res.data.meta_description_ar ||
          `استمع لأحدث ريمكسات ${display} من DJ SPY ثم اشترِ لتحميل فوري بأعلى جودة. تعديلات وريمكسات دي جي عربية جاهزة للحفلات.`;
      }
      const canonicalBase = lang === "ar" ? "/ar/genre/" : "/genre/";
      setHead(title, desc, `${canonicalBase}${res.data.slug}`);
      // Related genres
      try {
        const g = await api.get("/tracks/genres");
        const list = (Array.isArray(g.data) ? g.data : []).filter(
          (row) => slugify(row.genre) !== res.data.slug,
        );
        setRelated(list.slice(0, 6));
      } catch { setRelated([]); }
    } catch (e) {
      if (e?.response?.status === 404) {
        setNotFound(true);
      } else if (e?.response?.status === 301) {
        const loc = e.response?.headers?.location;
        if (loc) navigate(loc, { replace: true });
      }
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, user]);

  // Genre is locked by the URL; only category + search inputs filter the tracks.
  const filteredTracks = useMemo(() => {
    if (!data) return [];
    const all = Array.isArray(data.tracks) ? data.tracks : [];
    return all.filter((t) => {
      // Category filter
      if (filters.category === "premium" && (t.is_free || Number(t.price) <= 0)) return false;
      if (filters.category === "free" && !(t.is_free || Number(t.price) <= 0)) return false;
      if (filters.category === "originals" && t.track_type !== "original") return false;
      // Search filter — title/artist/genre/description
      if (filters.q) {
        const needle = filters.q.toLowerCase();
        const hay = `${t.title || ""} ${t.artist || ""} ${t.genre || ""} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [data, filters]);

  if (notFound) {
    return (
      <>
        <Nav />
        <main
          data-testid="page-genre-404"
          className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10 flex flex-col items-center justify-center"
        >
          <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// 404</p>
          <h1 className="font-anton text-5xl md:text-7xl mt-3">GENRE NOT FOUND</h1>
          <p className="mt-4 text-white/60 max-w-md text-center">
            This genre has no live releases yet, or the URL is wrong.
          </p>
          <Link to="/music" className="btn-primary mt-6 text-sm">
            &larr; Back to the catalogue
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Nav />
      <main
        data-testid={`page-genre-${slug}`}
        className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10"
      >
        <div className="max-w-[1400px] mx-auto">
          {!data && (
            <div className="flex items-center gap-3 text-white/50 font-mono text-xs tracking-widest mt-10" data-testid="genre-loading">
              <Loader2 size={14} className="animate-spin" /> LOADING GENRE…
            </div>
          )}
          {data && (
            <>
              <PageHeader
                kicker={`GENRE / ${data.display_name.toUpperCase()}`}
                title={data.display_name.toUpperCase()}
                lead={data.intro}
                testidPrefix="genre"
              />
              <Breadcrumbs
                items={[
                  { name: "Home", path: "/" },
                  { name: "Catalogue", path: "/music" },
                  { name: data.display_name, path: `/genre/${data.slug}` },
                ]}
                testid="genre-crumbs"
              />

              {data.tracks.length > 0 && (
                <FilterBar
                  value={filters}
                  onChange={setFilters}
                  genres={[]}
                  lockCategory={false}
                  testidPrefix="genre-filter"
                  resultCount={filteredTracks.length}
                />
              )}

              {filteredTracks.length > 0 && <div className="mt-2" />}

              <section className="mt-2" data-testid="genre-tracks-section">
                <header className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
                  <div>
                    <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// {data.display_name.toUpperCase()}</p>
                    <h2 className="font-anton text-3xl md:text-5xl mt-2">Tracks ({filteredTracks.length})</h2>
                  </div>
                </header>
                {data.tracks.length === 0 ? (
                  <EmptyState label="No tracks in this genre yet." testid="genre-empty" />
                ) : filteredTracks.length === 0 ? (
                  <EmptyState label="No tracks match your filters in this genre." testid="genre-empty-filtered" />
                ) : (
                  <TrackGrid tracks={filteredTracks} actions={actions} cart={cart} onDone={load} />
                )}
              </section>

              {/* Stage 4 — internal linking: published blog posts in this
                  genre. Hidden entirely when zero posts exist. */}
              {Array.isArray(data.related_posts) && data.related_posts.length > 0 && (
                <section className="mt-16" data-testid="genre-related-posts-section">
                  <header className="mb-6">
                    <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// FROM THE JOURNAL</p>
                    <h2 className="font-anton text-3xl md:text-5xl mt-2">
                      Blog posts about {data.display_name}
                    </h2>
                  </header>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="genre-related-posts-list">
                    {data.related_posts.map((post) => (
                      <Link
                        key={post.slug}
                        to={`/blog/${post.slug}`}
                        data-testid={`genre-related-post-${post.slug}`}
                        className="flex items-center gap-3 hairline rounded-xl p-3 bg-white/[0.02] hover:border-red-brand/60 transition-colors"
                      >
                        {post.cover_url ? (
                          <img
                            src={post.cover_url}
                            alt=""
                            className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-white/5 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-display text-sm text-white leading-tight line-clamp-2">
                            {post.title}
                          </p>
                          <p className="font-mono text-[10px] tracking-widest text-red-brand mt-1">
                            READ &rarr;
                          </p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {related.length > 0 && (
                <section className="mt-16" data-testid="genre-related-section">
                  <header className="mb-6">
                    <p className="font-mono text-xs text-red-brand tracking-[0.3em]">// {t("catalog.exploreMore", { defaultValue: "EXPLORE MORE" })}</p>
                    <h2 className="font-anton text-3xl md:text-5xl mt-2">{t("catalog.relatedGenres", { defaultValue: "Related Genres" })}</h2>
                  </header>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="genre-related-list">
                    {related.map((g) => {
                      const rslug = slugify(g.genre);
                      return (
                        <Link
                          key={rslug}
                          to={`/genre/${rslug}`}
                          data-testid={`genre-related-${rslug}`}
                          className="block hairline rounded-xl px-4 py-3 bg-white/[0.02] hover:border-red-brand/60 transition-colors"
                        >
                          <p className="font-display text-base text-white leading-tight">{g.genre}</p>
                          <p className="font-mono text-[10px] tracking-widest text-white/40 mt-1">
                            {g.count} TRACK{g.count === 1 ? "" : "S"}
                          </p>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}
            </>
          )}
        </div>
        <actions.AudioElement />
      </main>
      <Footer />
    </>
  );
}
